import moment from 'moment';
import { Dayparts } from 'core/dayparts/Dayparts';
import { DeviceAvailabilityCalendarPoint } from 'core/deviceManagement/DeviceManagement';
import { hasScheduledHoursOnDate } from './availabilityDayparts';
import { AVAILABILITY_DATE_FORMAT } from './availabilityFormat';
import styles from './deviceManagement.module.scss';

/**
 * 月曆上每一格的色階。
 *
 * 六種涵蓋了所有會畫出來的格子。刻意沒有第七種給「沒有營業時段資料」的點位 ——
 * 那種點位整個月都會是同一種狀態，逐日上色沒有任何資訊，而多一個灰階又會跟
 * 「非投放時段」混在一起。它改成整塊換一句說明，見 AvailabilityCalendarPanel。
 */
export enum AvailabilityLevel {
  SOLD_OUT = 'soldOut',
  LOW = 'low',
  MEDIUM = 'medium',
  AMPLE = 'ample',
  NOT_SCHEDULED = 'notScheduled',
  OUT_OF_RANGE = 'outOfRange'
}

export const AVAILABILITY_LEVEL_I18N: Record<AvailabilityLevel, string> = {
  [AvailabilityLevel.SOLD_OUT]: 'deviceManagement.legends.soldOut',
  [AvailabilityLevel.LOW]: 'deviceManagement.legends.low',
  [AvailabilityLevel.MEDIUM]: 'deviceManagement.legends.medium',
  [AvailabilityLevel.AMPLE]: 'deviceManagement.legends.ample',
  [AvailabilityLevel.NOT_SCHEDULED]: 'deviceManagement.legends.notScheduled',
  [AvailabilityLevel.OUT_OF_RANGE]: 'deviceManagement.legends.outOfRange'
};

/** 圖例的順序，從最缺貨到最不相關。 */
export const AVAILABILITY_LEGEND: AvailabilityLevel[] = [
  AvailabilityLevel.SOLD_OUT,
  AvailabilityLevel.LOW,
  AvailabilityLevel.MEDIUM,
  AvailabilityLevel.AMPLE,
  AvailabilityLevel.NOT_SCHEDULED,
  AvailabilityLevel.OUT_OF_RANGE
];

export const AVAILABILITY_LOW_THRESHOLD = 0.3;
export const AVAILABILITY_MEDIUM_THRESHOLD = 0.6;

/**
 * 一個可售率落在哪一個色階。月曆的格子與彙總的數字共用這一支，
 * 同一個比率在兩處不會被畫成兩種顏色。
 */
export function availabilityLevelOfRate(rate: number): AvailabilityLevel {
  if (rate <= 0) {
    return AvailabilityLevel.SOLD_OUT;
  }
  if (rate < AVAILABILITY_LOW_THRESHOLD) {
    return AvailabilityLevel.LOW;
  }
  if (rate < AVAILABILITY_MEDIUM_THRESHOLD) {
    return AvailabilityLevel.MEDIUM;
  }
  return AvailabilityLevel.AMPLE;
}

/**
 * 可售率畫成一個大字時的顏色，與月曆格子同一組色階。彙總那一列與詳情月曆
 * 都用這一支，同一個比率在幾處不會被畫成幾種顏色。
 */
export function availabilityRateClassName(rate: number): string {
  switch (availabilityLevelOfRate(rate)) {
    case AvailabilityLevel.SOLD_OUT:
      return styles.rateSoldOut;
    case AvailabilityLevel.LOW:
      return styles.rateLow;
    case AvailabilityLevel.MEDIUM:
      return styles.rateMedium;
    default:
      return styles.rateAmple;
  }
}

export type AvailabilityCalendarContext = {
  startDate: string;
  endDate: string;
  dayparts: Dayparts;
};

/**
 * 某一天要畫成哪一種色階。
 *
 * 判斷順序是刻意的：走期外的日子不管有沒有排時段都是走期外；走期內但那個星期
 * 一格都沒選，才是非投放時段。後端對沒排到的日子回 null，前端自己也能從時段表
 * 判出同一件事，兩邊對不上時以「沒排時段」為準 —— 少畫一格總比畫出一個沒買的
 * 時段的可售率好。
 */
export function resolveAvailabilityLevel(
  date: string,
  point: DeviceAvailabilityCalendarPoint | undefined,
  context: AvailabilityCalendarContext
): AvailabilityLevel {
  const day = moment(date, AVAILABILITY_DATE_FORMAT);
  const start = moment(context.startDate, AVAILABILITY_DATE_FORMAT);
  const end = moment(context.endDate, AVAILABILITY_DATE_FORMAT);
  if (day.isBefore(start, 'day') || day.isAfter(end, 'day')) {
    return AvailabilityLevel.OUT_OF_RANGE;
  }
  if (!hasScheduledHoursOnDate(context.dayparts, date)) {
    return AvailabilityLevel.NOT_SCHEDULED;
  }
  const rate = point?.availabilityRate;
  if (rate === undefined || rate === null) {
    return AvailabilityLevel.NOT_SCHEDULED;
  }
  return availabilityLevelOfRate(rate);
}

/** 走期涵蓋到的月份，由早到晚，格式 YYYY-MM。 */
export function monthsInRange(startDate: string, endDate: string): string[] {
  const cursor = moment(startDate, AVAILABILITY_DATE_FORMAT).startOf('month');
  const last = moment(endDate, AVAILABILITY_DATE_FORMAT).startOf('month');
  const months: string[] = [];
  while (cursor.isSameOrBefore(last, 'month')) {
    months.push(cursor.format('YYYY-MM'));
    cursor.add(1, 'month');
  }
  return months.length > 0 ? months : [moment().format('YYYY-MM')];
}

/**
 * 一個月的格線，每一列七天。
 *
 * 每列從週一開始，與時段表的星期編號同一套，這樣「週末全天」在兩張表上是同兩欄。
 * 補到整列的日子照樣回傳真實日期，它們會落在走期外而被畫成走期外那一色。
 */
export function buildMonthGrid(month: string): string[][] {
  const first = moment(month, 'YYYY-MM').startOf('month');
  const cursor = first.clone().startOf('isoWeek');
  const lastDay = first.clone().endOf('month');
  const end = lastDay.clone().endOf('isoWeek');
  const weeks: string[][] = [];
  while (cursor.isSameOrBefore(end, 'day')) {
    const week: string[] = [];
    for (let index = 0; index < 7; index++) {
      week.push(cursor.format(AVAILABILITY_DATE_FORMAT));
      cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

/** 這一天是不是落在正在顯示的那個月，用來把補進來的日子畫淡一點。 */
export function isDateInMonth(date: string, month: string): boolean {
  return date.startsWith(`${month}-`);
}
